import React, { useMemo, useState } from 'react';
import { CheckCircleIcon, Loader2Icon, AlertCircleIcon, ClipboardCheckIcon } from 'lucide-react';
import { FieldCell, FieldConfig } from './FieldInput';
const DEMO_FIELDS: FieldConfig[] = [{
  id: 'fullName',
  label: 'Full Name',
  type: 'text'
}, {
  id: 'dob',
  label: 'Date of Birth',
  type: 'date'
}, {
  id: 'income',
  label: 'Annual Income',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'employmentStatus',
  label: 'Employment Status',
  type: 'select',
  options: ['Employed', 'Self-Employed', 'Retired', 'Unemployed']
}, {
  id: 'employerName',
  label: 'Employer Name',
  type: 'text'
}, {
  id: 'loanAmount',
  label: 'Loan Amount Requested',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'propertyAddress',
  label: 'Property Address',
  type: 'text'
}, {
  id: 'loanType',
  label: 'Loan Type',
  type: 'select',
  options: ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo']
}];
export function RequiredFieldsCard() {
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Calculate progress
  const totalCount = DEMO_FIELDS.length;
  const completedCount = Object.keys(savedValues).filter(k => savedValues[k].trim() !== '').length;
  const remainingCount = totalCount - completedCount;
  const isAllComplete = remainingCount === 0;
  // Filter to only show incomplete fields (not yet saved with a value)
  const visibleFields = useMemo(() => {
    return DEMO_FIELDS.filter(field => {
      const saved = savedValues[field.id] || '';
      return saved.trim() === '';
    });
  }, [savedValues]);
  // Check if there are unsaved changes
  const isDirty = useMemo(() => {
    return Object.entries(pendingEdits).some(([key, val]) => {
      const savedVal = savedValues[key] || '';
      return val.trim() !== savedVal.trim();
    });
  }, [pendingEdits, savedValues]);
  const handleFieldChange = (id: string, value: string) => {
    setPendingEdits(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSave = () => {
    setIsSaving(true);
    // Simulate network request
    setTimeout(() => {
      setSavedValues(prev => {
        const newSaved = {
          ...prev
        };
        Object.entries(pendingEdits).forEach(([key, val]) => {
          newSaved[key] = val;
        });
        return newSaved;
      });
      setPendingEdits({});
      setIsSaving(false);
    }, 600);
  };
  const handleCancel = () => {
    setPendingEdits({});
  };
  return <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200 overflow-hidden max-w-4xl mx-auto flex flex-col relative" data-id="element-22">
      {/* Top Accent Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 to-indigo-500" data-id="element-23" />

      {/* Content wrapper with top padding to clear accent bar */}
      <div className="pt-1" data-id="element-24">
        {/* Save/Cancel Toolbar */}
        {isDirty && <div className="px-6 py-3 bg-indigo-50/90 backdrop-blur-sm border-b border-indigo-100 flex items-center justify-between animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-25">
            <div className="flex items-center gap-2.5 text-sm text-indigo-900 font-medium" data-id="element-26">
              <AlertCircleIcon className="w-4 h-4 text-indigo-600" data-id="element-27" />
              <span data-id="element-28">You have unsaved changes</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-29">
              <button onClick={handleCancel} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50" data-id="element-30">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center justify-center px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm shadow-indigo-300/50 transition-all disabled:opacity-70 min-w-[100px]" data-id="element-31">
                {isSaving ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-32" /> : 'Save Changes'}
              </button>
            </div>
          </div>}

        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-33">
          <div data-id="element-34">
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight" data-id="element-35">
              Required Information
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-36">
              {remainingCount === 0 ? 'All fields complete' : `Please complete the remaining ${remainingCount} field${remainingCount !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {/* Completion Banner */}
        {isAllComplete && !isDirty && <div className="px-6 py-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 flex items-start sm:items-center gap-4" data-id="element-37">
            <div className="bg-emerald-100 p-2.5 rounded-full flex-shrink-0" data-id="element-38">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-39" />
            </div>
            <div data-id="element-40">
              <h3 className="text-base font-semibold text-emerald-900" data-id="element-41">
                Application Ready
              </h3>
              <p className="text-sm text-emerald-700 mt-0.5" data-id="element-42">
                All required fields are complete. Your application is ready for
                review by your loan officer.
              </p>
            </div>
          </div>}

        {/* Two-Column Grid or Empty State */}
        {visibleFields.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 bg-white" data-id="element-43">
            {visibleFields.map((field, index) => {
          const currentValue = pendingEdits[field.id] ?? savedValues[field.id] ?? '';
          const savedValue = savedValues[field.id] ?? '';
          // Calculate grid position for border logic
          const isLeftColumn = index % 2 === 0;
          const totalRows = Math.ceil(visibleFields.length / 2);
          const currentRow = Math.floor(index / 2);
          const isLastRow = currentRow === totalRows - 1;
          // For single item in last row, it spans full width conceptually
          const isLastItem = index === visibleFields.length - 1;
          return <FieldCell key={field.id} field={field} value={currentValue} savedValue={savedValue} onChange={val => handleFieldChange(field.id, val)} isLastInRow={!isLeftColumn || isLastItem && visibleFields.length % 2 === 1} isLastRow={isLastRow} data-id="element-44" />;
        })}
          </div> : <div className="px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-45">
            <div className="bg-slate-100 p-4 rounded-full mb-4" data-id="element-46">
              <ClipboardCheckIcon className="w-7 h-7 text-slate-400" data-id="element-47" />
            </div>
            <p className="text-base font-semibold text-slate-700" data-id="element-48">
              No Required Fields Missing
            </p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-49">
              All required information has been provided.
            </p>
          </div>}
      </div>
    </div>;
}